import React, {Component} from "react";
import {loadScript} from "../../utils/browser";
import {UserContext} from "../../context/UserContext";
import {Loading} from "../../components/Loading";
import URI from 'urijs';
import {AdobeAnalyticsContextData} from "../../context/AdobeAnalyticsContext";
import queryString from "query-string";
import {ProfileEntity} from "../../data/entities/Connect/ProfileEntity";

interface State {
    loading: boolean;
}

interface Props {
    applicationId: string | undefined;
    environment: string | undefined;
    genomeId: string | undefined;
    locale: string | undefined;
    analytics: AdobeAnalyticsContextData;
}

interface UbisoftConnectSDK {
    readonly getTicket: () => any;
    readonly getProfiles: (ticket: string, sessionId: string, userId: string) => any;
}

export interface UbisoftConnectResponse {
    readonly status: 'ok' | 'error';
    readonly payload: {
        readonly userId: string;
        readonly expiration: string;
        readonly sessionId: string;
        readonly ticket: string;
    };
}

export interface UbisoftGetProfilesResponse {
    readonly status: 'ok' | 'error';
    readonly payload: {
        profiles: ProfileEntity[];
    };
}

export default class ConnectAuth extends Component<Props, State> {
    static contextType = UserContext;
    context!: React.ContextType<typeof UserContext>;
    SDK!: UbisoftConnectSDK;

    refreshTimer: ReturnType<typeof setInterval> | null = null;

    private sdkUrl = "https://ubistatic2-a.akamaihd.net/uplay-connect/v3/prod/default/sdk/connectSdkPublic.js";

    constructor(props: Props) {
        super(props);

        this.state = {
            loading: true,
        };
    }

    componentDidMount(): void {
        loadScript(this.sdkUrl).then(this.init);
    }

    componentWillUnmount() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
    }

    init = () => {
        const {environment, applicationId, genomeId, locale} = this.props;

        const connect: any = (window as any).Connect;
        const nextUrl: string = new URI(window.location.origin).filename('index.html').href();
        const options: object = {
            env: environment ? environment.toUpperCase() : null,
            appId: applicationId,
            genomeId: genomeId,
            lang: locale,
            nextUrl: encodeURIComponent(nextUrl),
            thirdPartyCookiesSupport: false
        };

        connect.init(options);
        connect.sdk.subscribe(this.subscribeToConnect);
        this.refreshTimer = setInterval(this.refreshTicket, 3600000);
    };

    subscribeToConnect = (sdk: UbisoftConnectSDK): void => {
        this.SDK = sdk;
        this.SDK.getTicket().subscribe((response: UbisoftConnectResponse) => {
            if (response.status === 'ok') {
                this.handleGetProfiles(response);
            } else {
                this.handleUserNotAuthenticated();
            }
        });
    };

    refreshTicket = (): void => {
        this.SDK.getTicket().subscribe((response: UbisoftConnectResponse) => {
            if (response.status === 'ok') {
                this.handleGetProfiles(response);
            } else {
                window.location.reload();
            }
        });
    };

    render(): JSX.Element {
        if (this.state.loading) {
            return <Loading/>;
        } else {
            return this.props.children as JSX.Element;
        }
    }

    handleGetProfiles = (response: UbisoftConnectResponse) => {
        let ticketData = response.payload;
        this.SDK.getProfiles(ticketData.ticket, ticketData.sessionId, ticketData.userId).subscribe((profilesResponse: UbisoftGetProfilesResponse) => {
            if(profilesResponse.status === "ok") {
                this.context.login(response.payload, profilesResponse.payload.profiles, () => {
                    this.setState({loading: false})
                });
            } else {
                window.location.reload();
            }
        });
    };

    handleUserNotAuthenticated = () => {
        this.props.analytics.track("accountevent", {
            ubisoftId: this.context.user.ticketData.userId,
            loggedIn: "false",
            loggedOut: "true",
            action: "page - login - logged out"
        });
        this.setState({loading: false});
    };

    static openConnectPopup = (locale: string): void => {
        const connectBaseUrls = {
            uat: "https://uat-connect.ubisoft.com/login",
            preprod: "https://connect.ubisoft.com/login",
            prod: "https://connect.ubisoft.com/login",
        };

        const environment: string = process.env.REACT_APP_UBISOFT_CONNECT_ENV || 'prod';
        const params = {
            appId: process.env.REACT_APP_APP_ID,
            genomeId: process.env.REACT_APP_GENOME_ID,
            lang: locale,
            nextUrl: `${window.location.origin}/connect-callback`
        };

        const popupUrl: string = `${connectBaseUrls[environment]}?${queryString.stringify(params)}`;

        const width: number = 480;
        const height: number = 720;

        //-- center popup ---
        let y: number = window.top.outerHeight / 2 + window.top.screenY - (height / 2);
        let x: number = window.top.outerWidth / 2 + window.top.screenX - (width / 2);

        window.open(popupUrl,"Ubisoft Connect","width=" + width + ",height=" + height + ",top=" + y + ",left=" + x + ",toolbar=no,status=no");
    };

    static triggerExternalLogin = () => {
        if (window.opener != null && window.opener.overlayWidget != null) {
            window.opener.postMessage("EXTERNAL_UBISOFT_CONNECT");
            window.close();
        }
    };
}